import { useRef } from "react";
import { useLocalSearchParams, usePathname } from "expo-router";
import { View } from "react-native";
import { Button, ButtonIcon, ButtonText } from "ui/gluestack/button";
import { AddIcon } from "ui/gluestack/icon";
import { CreateEditMedicationModal } from "../modals/CreateEditMedicationModal";
import { ResidentCurrentMedicationsTemplate } from "./ResidentCurrentMedicationsTemplate";
import { MedicationHistoryTemplate } from "./MedicationHistoryTemplate";
import {
  getResidentMedicationRoute,
  MEDICATION_FILTER_LABELS,
  MedicationFilter,
} from "app/services/api/types/medications/enums/MedicationFilterEnum";
import { MedicationLogTemplate } from "./MedicationLogTemplate";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { MenuSection } from "../../reporting/organisms/MenuSection";
import { PendingMedicationsSignatureTemplate } from "./PendingMedicationsSignatureTemplate";
import { AdministerBulkMedicationModal } from "../modals/AdministerBulkMedicationModal";
import { ResidentMedicationsProvider } from "../provider/ResidentMedicationsProvider";

export function ResidentMedicationsTemplate() {
  const ref =
    useRef<React.ComponentRef<typeof CreateEditMedicationModal>>(null);
  const administerBulkRef =
    useRef<React.ComponentRef<typeof AdministerBulkMedicationModal>>(null);
  const handleShowModal = () => {
    ref.current?.show();
  };
  const handleShowAdministerBulkModal = () => {
    administerBulkRef.current?.show();
  };

  const pathname = usePathname();
  const { id } = useLocalSearchParams();

  const MEDICATION_FILTER_OPTIONS = Object.values(MedicationFilter).map(
    (filter) => ({
      label: MEDICATION_FILTER_LABELS[filter],
      value: getResidentMedicationRoute(id as string, filter),
    }),
  );

  const currentOption = MEDICATION_FILTER_OPTIONS.find(
    (option) => option.value === pathname,
  );

  const currentPath = currentOption?.value ?? `/residents/${id}/medications`;

  const medicationMenuOptions = Object.values(MedicationFilter).map(
    (filter) => {
      const href = getResidentMedicationRoute(id as string, filter);

      return {
        label: MEDICATION_FILTER_LABELS[filter],
        href,
        selected: href === currentPath,
      };
    },
  );

  const filterType = currentOption?.value ?? `/residents/${id}/medications`;

  const getFilteredComponent = () => {
    switch (filterType) {
      case getResidentMedicationRoute(id as string, MedicationFilter.CURRENT):
        return <ResidentCurrentMedicationsTemplate />;
      case getResidentMedicationRoute(id as string, MedicationFilter.LOG):
        return <MedicationLogTemplate />;
      case getResidentMedicationRoute(id as string, MedicationFilter.HISTORY):
        return <MedicationHistoryTemplate />;
      case getResidentMedicationRoute(
        id as string,
        MedicationFilter.PENDING_SIGNATURE,
      ):
        return <PendingMedicationsSignatureTemplate />;
      default:
        return null;
    }
  };

  return (
    <ResidentMedicationsProvider
      onFinishedBulkAdminister={() => administerBulkRef.current?.close()}
      isPaginated={false}
    >
      <View className="resident-container">
        <View className="flex flex-col sm:flex-row gap-4">
          <View className="sm:w-56 flex flex-col gap-4">
            <MenuSection options={medicationMenuOptions}></MenuSection>

            <View className="flex flex-col gap-2">
              <PermissionCheck permission={PermissionsEnums.CREATE_MEDICATION}>
                <Button
                  variant="outline"
                  action="primary"
                  onPress={handleShowModal}
                  size="lg"
                >
                  <ButtonText>Prescribe medication</ButtonText>
                  <ButtonIcon as={AddIcon} />
                </Button>
              </PermissionCheck>
              <Button
                variant="solid"
                action="primary"
                onPress={handleShowAdministerBulkModal}
                size="lg"
              >
                <ButtonText>Administer medications</ButtonText>
              </Button>
            </View>
          </View>

          <View className="flex flex-1 flex-col">{getFilteredComponent()}</View>
        </View>

        <CreateEditMedicationModal ref={ref} />
        <AdministerBulkMedicationModal ref={administerBulkRef} />
      </View>
    </ResidentMedicationsProvider>
  );
}
